const { jQuery: $ } = window;

interface GPAATravelEstimateOpts {
	formId: number;
	fieldId: number;
	originFieldId: number;
	destinationFieldId: number;
	/** Input number (1 = distance, 2 = time) mapped to its unit abbreviation. */
	activeInputs: { [inputId: string]: string };
	/** Content template, resolved server-side, using the {distance} and {time} tags. */
	template: string;
	unitLabels: { distance: string; time: string };
	emptyPlaceholder: string;
}

interface GPAATravelEstimateResult {
	distance: number | null;
	time: number | null;
	polyline: string;
}

/**
 * Keeps a Travel Estimate field's value in sync with the two Address fields it is configured to
 * measure between.
 *
 * The route itself is computed server-side (Routes API is REST-only, and this keeps the API key off
 * the page), so this class is only responsible for noticing address changes, asking for a route,
 * and writing the result into the hidden inputs Gravity Forms reads.
 */
class GP_Address_Autocomplete_Travel_Estimate {
	opts: GPAATravelEstimateOpts;

	debounceTimer: ReturnType<typeof setTimeout> | undefined;

	/** Incremented per request so a slow response can't overwrite a newer one. */
	requestSeq = 0;

	constructor(opts: GPAATravelEstimateOpts) {
		this.opts = opts;

		this.bindAddressFields();

		// Addresses may already be filled (multi-page, save & continue, prepopulation).
		this.scheduleUpdate();
	}

	get $form() {
		return $(`#gform_${this.opts.formId}`);
	}

	addressInputSelector(addressFieldId: number) {
		return `#field_${this.opts.formId}_${addressFieldId} input, #field_${this.opts.formId}_${addressFieldId} select`;
	}

	bindAddressFields() {
		const selector = [
			this.addressInputSelector(this.opts.originFieldId),
			this.addressInputSelector(this.opts.destinationFieldId),
		].join(', ');

		this.$form.on('change', selector, () => this.scheduleUpdate());

		// Autocomplete fills several inputs in one go; a single recalculation after it settles is enough.
		window.gform.addAction(
			'gpaa_fields_filled',
			(
				place: unknown,
				instance: unknown,
				formId: number,
				fieldId: number
			) => {
				if (
					formId !== this.opts.formId ||
					(fieldId !== this.opts.originFieldId &&
						fieldId !== this.opts.destinationFieldId)
				) {
					return;
				}

				this.scheduleUpdate();
			}
		);
	}

	scheduleUpdate() {
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
		}

		this.debounceTimer = setTimeout(() => this.update(), 500);
	}

	/**
	 * Collect both address fields' inputs under the names the server expects (`input_{id}_{n}`),
	 * matching what a real submission would send.
	 */
	getRequestData() {
		const data: { [key: string]: string } = {
			action: 'gpaa_compute_route',

			nonce: window.GP_ADDRESS_AUTOCOMPLETE_CONSTANTS.compute_route_nonce,
			// eslint-disable-next-line camelcase
			form_id: String(this.opts.formId),
			// eslint-disable-next-line camelcase
			field_id: String(this.opts.fieldId),
		};

		[this.opts.originFieldId, this.opts.destinationFieldId].forEach(
			(addressFieldId) => {
				$(this.addressInputSelector(addressFieldId)).each((_, el) => {
					const input = el as HTMLInputElement;
					// Address inputs are named `input_{fieldId}.{n}`; the server reads `input_{fieldId}_{n}`.
					const match = input.name?.match(
						new RegExp(`^input_${addressFieldId}\\.(\\d+)$`)
					);

					if (match) {
						data[`input_${addressFieldId}_${match[1]}`] =
							input.value;
					}
				});
			}
		);

		return data;
	}

	update() {
		const seq = ++this.requestSeq;

		$.post(
			window.GP_ADDRESS_AUTOCOMPLETE_CONSTANTS.ajaxurl,
			this.getRequestData()
		)
			.done((response: { success: boolean; data: any }) => {
				if (seq !== this.requestSeq) {
					return;
				}

				if (response.success) {
					this.setResult(response.data);
				} else {
					// An incomplete address is an expected in-progress state, not an error to shout about.
					this.clear(
						response.data?.incomplete ? '' : response.data?.message
					);
				}
			})
			.fail(() => {
				if (seq === this.requestSeq) {
					this.clear();
				}
			});
	}

	$input(inputId: string) {
		return this.$form.find(
			`input[name="input_${this.opts.fieldId}.${inputId}"]`
		);
	}

	$content() {
		return $(
			`#field_${this.opts.formId}_${this.opts.fieldId} .gpaa_travel_estimate_content`
		);
	}

	renderContent(result: GPAATravelEstimateResult | null) {
		const $content = this.$content();

		if (!result || (result.distance === null && result.time === null)) {
			$content.val(this.opts.emptyPlaceholder);
			return;
		}

		const text = this.opts.template
			.split('{distance}')
			.join(
				result.distance === null
					? ''
					: `${result.distance} ${this.opts.unitLabels.distance}`
			)
			.split('{time}')
			.join(
				result.time === null
					? ''
					: `${result.time} ${this.opts.unitLabels.time}`
			);

		$content.val(text);
	}

	setValue(inputId: string, value: string) {
		const $input = this.$input(inputId);

		// Only fire when the value actually changed. Autocomplete emits a change per address input
		// as it fills a field, so without this guard every fill pushed a fresh round of change
		// events into Gravity Forms' calculation and conditional logic pipeline, and re-rendered
		// the readout, for a value that was usually identical.
		if (String($input.val()) === value) {
			return;
		}

		// Triggering change is what makes Gravity Forms re-run calculations and conditional logic.
		$input.val(value).trigger('change');
	}

	setResult(result: GPAATravelEstimateResult) {
		if (result.distance !== null) {
			this.setValue('1', String(result.distance));
		}

		if (result.time !== null) {
			this.setValue('2', String(result.time));
		}

		this.$form
			.find(`input[name="gpaa_route_${this.opts.fieldId}"]`)
			.val(result.polyline);

		this.renderContent(result);
		this.setError('');

		/**
		 * Action that fires when a Travel Estimate field receives a new route.
		 *
		 * @param {Object} result  The computed distance, time and encoded polyline.
		 * @param {number} formId  The current form ID.
		 * @param {number} fieldId The Travel Estimate field ID.
		 *
		 * @since 2.0
		 */
		window.gform.doAction(
			'gpaa_travel_estimate_updated',
			result,
			this.opts.formId,
			this.opts.fieldId
		);
	}

	clear(message = '') {
		Object.keys(this.opts.activeInputs).forEach((inputId) =>
			this.setValue(inputId, '')
		);

		this.$form
			.find(`input[name="gpaa_route_${this.opts.fieldId}"]`)
			.val('');

		// Without this the field keeps displaying the previous estimate after it has been cleared,
		// which is exactly the misleading result an incomplete address is supposed to avoid.
		this.renderContent(null);

		this.setError(message);

		window.gform.doAction(
			'gpaa_travel_estimate_updated',
			null,
			this.opts.formId,
			this.opts.fieldId
		);
	}

	setError(message: string) {
		const $container = $(
			`#field_${this.opts.formId}_${this.opts.fieldId} .ginput_container_travel_estimate`
		);

		const $existing = $container.find('.gpaa_travel_estimate_error');

		// Removing and re-appending an identical message resizes the field for no reason, which
		// shifts everything below it on the page.
		if ($existing.text() === message) {
			return;
		}

		$existing.remove();

		if (message) {
			$container.append(
				$('<div class="gpaa_travel_estimate_error"></div>').text(
					message
				)
			);
		}
	}
}

window.GP_Address_Autocomplete_Travel_Estimate = GP_Address_Autocomplete_Travel_Estimate;

export {};
